<template>
  <div
    id="holder-drawer"
    aria-hidden="true"
    v-show="this.open"
    class="fixed top-0 left-0 w-full max-w-[410px] z-40 h-screen p-4 overflow-y-auto transition-transform -translate-x-full bg-neutral-900"
  >
    <div class="flex gap-3 items-center">
      <h5 class="text-base font-semibold uppercase text-gray-400">
        Holder distribution
      </h5>
      <tooltip text="Reload list holder" @click="this.refreshListHolder">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          class="w-[21px] text-gray-500 cursor-pointer"
          :class="{
            'animate-spin': this.onRefreshHolder,
          }"
        >
          <path
            fill-rule="evenodd"
            d="M4.755 10.059a7.5 7.5 0 0 1 12.548-3.364l1.903 1.903h-3.183a.75.75 0 1 0 0 1.5h4.992a.75.75 0 0 0 .75-.75V4.356a.75.75 0 0 0-1.5 0v3.18l-1.9-1.9A9 9 0 0 0 3.306 9.67a.75.75 0 1 0 1.45.388Zm15.408 3.352a.75.75 0 0 0-.919.53 7.5 7.5 0 0 1-12.548 3.364l-1.902-1.903h3.183a.75.75 0 0 0 0-1.5H2.984a.75.75 0 0 0-.75.75v4.992a.75.75 0 0 0 1.5 0v-3.18l1.9 1.9a9 9 0 0 0 15.059-4.035.75.75 0 0 0-.53-.918Z"
            clip-rule="evenodd"
          />
        </svg>
      </tooltip>
      <div
        class="cursor-pointer"
        @click="this.openImportMyHolderAddressModal = true"
      >
        <tooltip text="Import my holder address">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-[21px] text-gray-500 hover:text-red-600"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M9 3.75H6.912a2.25 2.25 0 0 0-2.15 1.588L2.35 13.177a2.25 2.25 0 0 0-.1.661V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 0 0-2.15-1.588H15M2.25 13.5h3.86a2.25 2.25 0 0 1 2.012 1.244l.256.512a2.25 2.25 0 0 0 2.013 1.244h3.218a2.25 2.25 0 0 0 2.013-1.244l.256-.512a2.25 2.25 0 0 1 2.013-1.244h3.859M12 3v8.25m0 0-3-3m3 3 3-3"
            />
          </svg>
        </tooltip>
      </div>
    </div>
    <button
      type="button"
      class="text-gray-400 bg-transparent rounded-lg text-sm w-8 h-8 absolute top-2.5 right-2.5 inline-flex items-center justify-center hover:bg-neutral-800 hover:text-white"
      @click="this.drawer.hide()"
    >
      <svg
        class="w-3 h-3"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 14 14"
      >
        <path
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
        />
      </svg>
    </button>
    <div class="py-4 overflow-y-auto text-gray-400">
      <div>
        <div
          v-for="(holder, index) in this.holders"
          :key="holder.address"
          class="flex items-center justify-between gap-2"
        >
          <div class="flex items-center gap-2">
            <div class="text-sm">{{ index + 1 }}.</div>
            <a
              :href="this.explorerUrlAddress(holder.address)"
              target="_blank"
              class="text-sm hover:text-blue-500 hover:underline"
            >
              {{ this.shortAddress(holder.address) }}
            </a>
            <div
              class="text-xs text-gray-300"
              v-if="this.isBondingCurve(holder.address)"
            >
              🏦
            </div>
            <div
              class="text-xs text-gray-300"
              v-if="this.isMyHolderAddress(holder.address)"
            >
              🤵
            </div>
          </div>
          <div class="text-sm">
            {{
              ((holder.amount / this.metadata.totalSupply) * 100).toFixed(2)
            }}%
          </div>
        </div>
      </div>
    </div>
  </div>
  <import-my-holder-address-modal
    :open="this.openImportMyHolderAddressModal"
    :my-holder-wallet="this.myHolderWallet"
  />
</template>

<script>
import { Drawer } from "flowbite";
import Tooltip from "@/components/Tooltip.vue";
import ImportMyHolderAddressModal from "@/components/Holder/ImportMyHolderAddressModal.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import { Bitquery } from "@/utils/bitquery";

export default {
  name: "HolderDrawer",
  components: { ImportMyHolderAddressModal, Tooltip },
  data() {
    return {
      drawer: null,
      holders: [],
      onRefreshHolder: false,
      openImportMyHolderAddressModal: false,
      myHolderWallet: [],
    };
  },
  computed: {
    ...mapGetters("network", [
      "shortAddress",
      "explorerUrlAddress",
      "getLocalStorage",
      "networkBitquery",
    ]),
    ...mapGetters("wallet", ["network", "wallets"]),
    ...mapState("solana/pumpfun", ["bondingCurve"]),
    ...mapState({
      metadata(state) {
        switch (this.network) {
          case "solana":
            return state.solana.metadata;
          case "ethereum":
          case "base":
          case "bsc":
          default:
            return state.ethereum.metadata;
        }
      },
      tokenAddress(state) {
        switch (this.network) {
          case "solana":
            return state.solana.tokenAddress;
          case "ethereum":
          case "base":
          case "bsc":
          default:
            return state.ethereum.tokenAddress;
        }
      },
      pairAddress(state) {
        switch (this.network) {
          case "solana":
            return state.solana.pairAddress;
          case "ethereum":
          case "base":
          case "bsc":
          default:
            return state.ethereum.pairAddress;
        }
      },
    }),
    open() {
      return this.$store.state.holderDistribution.open;
    },
  },
  watch: {
    async open() {
      if (this.open === true) {
        this.drawer.show();
        this.myHolderWallet = this.getLocalStorage["myHolderWallet"] ?? [];
        await this.refreshListHolder();
      } else {
        this.drawer.hide();
      }
    },
  },
  methods: {
    ...mapActions("holderDistribution", ["show", "hide"]),
    ...mapActions("solana", ["getListHolder"]),
    isBondingCurve(address) {
      return !!(this.bondingCurve && this.bondingCurve.toBase58() === address);
    },
    isMyHolderAddress(address) {
      const allWallets = [
        ...new Map(
          [...this.wallets, ...this.myHolderWallet].map((item) => [
            item.address,
            item,
          ])
        ).values(),
      ];
      return allWallets.some(
        (item) => item.address.toLowerCase() === address.toLowerCase()
      );
    },
    async refreshListHolder() {
      this.onRefreshHolder = true;
      if (this.tokenAddress) {
        switch (this.network) {
          case "solana":
            this.holders = await this.getListHolder();
            break;
          case "ethereum":
          case "base":
          case "bsc":
          default:
            this.holders = await new Bitquery().getTokenHolders(
              this.tokenAddress,
              this.metadata.totalSupply * (1 / 1000),
              this.networkBitquery
            );
            break;
        }
      }
      this.onRefreshHolder = false;
    },
  },
  mounted() {
    this.drawer = new Drawer(document.getElementById("holder-drawer"), {
      placement: "left",
      backdrop: false,
      bodyScrolling: false,
      edge: false,
      edgeOffset: "",
      onHide: () => {
        this.hide();
      },
    });
  },
  beforeMount() {},
  beforeUnmount() {
    this.hide();
  },
};
</script>
